import { FC, HTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react'
import { Richtext } from 'storyblok-js-client'
import { Typography } from '@components/design'
import Link from 'next/link'
import Storyblok from '@lib/storyblok'
import HTMLParser from 'react-html-map'

const Anchor: FC<AnchorHTMLAttributes<HTMLAnchorElement>> = ({
  href,
  children,
  ...rest
}) => {
  if (href?.startsWith('/')) {
    return (
      <Link href={href} {...rest}>
        <span className="underline text-rust uppercase">{children}</span>
      </Link>
    )
  } else {
    return (
      <a className="underline text-rust uppercase" href={href} {...rest}>
        {children}
      </a>
    )
  }
}

const Paragraph: FC<HTMLAttributes<HTMLParagraphElement>> = ({ children }) => {
  return <Typography variant="body1">{children}</Typography>
}

const HeadingOne: FC<HTMLAttributes<HTMLHeadingElement>> = ({ children }) => {
  return (
    <Typography className="font-bold" variant="h1" component="h1">
      {children}
    </Typography>
  )
}

const HeadingTwo: FC<HTMLAttributes<HTMLHeadingElement>> = ({ children }) => {
  return (
    <Typography className="font-bold" variant="h2" component="h2">
      {children}
    </Typography>
  )
}
const HeadingThree: FC<HTMLAttributes<HTMLHeadingElement>> = ({ children }) => {
  return (
    <Typography className="font-bold" variant="h3" component="h3">
      {children}
    </Typography>
  )
}

const HeadingFour: FC<HTMLAttributes<HTMLHeadingElement>> = ({ children }) => {
  return (
    <Typography className="font-bold" variant="h4" component="h4">
      {children}
    </Typography>
  )
}

const HeadingFive: FC<HTMLAttributes<HTMLHeadingElement>> = ({ children }) => {
  return (
    <Typography className="font-bold" variant="h5" component="h5">
      {children}
    </Typography>
  )
}
const HeadingSix: FC<HTMLAttributes<HTMLHeadingElement>> = ({ children }) => {
  return (
    <Typography className="font-bold" variant="h5" component="h6">
      {children}
    </Typography>
  )
}

const Strong: FC<HTMLAttributes<HTMLElement>> = ({ children }) => {
  return (
    <span className="font-bold">
      {children}
    </span>
  )
}

const Italic: FC<HTMLAttributes<HTMLElement>> = ({ children }) => {
  return (
    <span className="italic">
      {children}
    </span>
  )
}

const Underline: FC<HTMLAttributes<HTMLElement>> = ({ children }) => {
  return (
    <span className="underline">
      {children}
    </span>
  )
}

const Span: FC<HTMLAttributes<HTMLElement>> = ({ children, ...rest }) => {
  return <div {...rest}>{children}</div>
}

const List: FC<any> = ({ children, ...rest }) => {
  return <span {...rest}>{children}</span>
}
const ListItem: FC<any> = ({ children }) => {
  return <Typography variant="body1">{children}</Typography>
}
interface RichTextRendererProps {
  richTextStoryblok?: Richtext
  html?: string
  mask?: { [key: string]: ReactNode }
}

const RichTextRenderer: FC<RichTextRendererProps> = (props) => {
  const { richTextStoryblok, html, mask } = props
  const nMask = {
    a: Anchor,
    p: Paragraph,
    b: Strong,
    i: Italic,
    u: Underline,
    span: Span,
    h1: HeadingOne,
    h2: HeadingTwo,
    h3: HeadingThree,
    h4: HeadingFour,
    h5: HeadingFive,
    h6: HeadingSix,
    ul: List,
    ol: List,
    li: ListItem,
    ...mask,
  }
  if (!richTextStoryblok && !html) {
    return null
  }

  const htmlText = richTextStoryblok
  ? Storyblok.richTextResolver.render(richTextStoryblok)
    : (html as string)
  
  return (
    <HTMLParser
      html={htmlText}
      decodeEntities
    >
      {nMask}
    </HTMLParser>
  )
}

export default RichTextRenderer
